/**
 * The end-of-session screen: the full Glory breakdown, ranked.
 *
 * Not just a winner. docs/07 Stage 4.1 is explicit that players need to see
 * *why* they lost — a player who can read "I held twice the land and still lost
 * on banked resources" has a reason to start another session, and a player who
 * only sees a name does not. So every kingdom gets a row and every row is
 * itemised into the four terms `gloryScore` actually sums.
 *
 * Rebuilt from scratch on each `show`, which happens once per session. The
 * create-once discipline the rest of the HUD follows exists because those
 * panels update at 10Hz; this one does not.
 */

import type { GloryScore, KingdomId, KingdomState, SessionEndedMessage } from "kngdm-shared";
import { rankStandings } from "kngdm-shared";

import { formatAmount, formatColor } from "./format";

export class EndScreen {
  readonly element: HTMLElement;

  private readonly card: HTMLElement;

  constructor() {
    this.element = el("div", "endscreen is-hidden");
    this.card = el("div", "endscreen__card panel");
    this.element.append(this.card);
  }

  show(
    message: SessionEndedMessage,
    kingdoms: readonly KingdomState[],
    ownKingdomId: KingdomId,
  ): void {
    const byId = new Map<KingdomId, KingdomState>();
    for (const kingdom of kingdoms) byId.set(kingdom.id, kingdom);

    // Ranked again on the client because the panel must not depend on the
    // server having sorted; `rankStandings` is the shared function that decides
    // ties, so this cannot disagree with the authoritative order.
    const standings = rankStandings(message.standings);
    const winner = findWinner(standings, byId);

    this.card.replaceChildren();
    this.card.append(
      this.buildHeader(message, winner, ownKingdomId, byId),
      buildTable(standings, byId, ownKingdomId),
      el(
        "p",
        "endscreen__note",
        "Glory = territory + buildings + banked resources + trade volume.",
      ),
      this.buildDismiss(),
    );

    this.element.classList.remove("is-hidden");
  }

  hide(): void {
    this.element.classList.add("is-hidden");
  }

  destroy(): void {
    this.element.remove();
  }

  private buildHeader(
    message: SessionEndedMessage,
    winner: KingdomId | null,
    ownKingdomId: KingdomId,
    byId: ReadonlyMap<KingdomId, KingdomState>,
  ): HTMLElement {
    const header = el("header", "endscreen__header");

    const won = winner !== null && winner === ownKingdomId;
    const title = el("h1", won ? "endscreen__title is-win" : "endscreen__title");
    title.textContent = won ? "Victory" : "Defeat";
    header.append(title);

    const winnerName = winner === null ? null : byId.get(winner)?.name ?? winner;
    const reason =
      message.result === "timer"
        ? "The session clock ran out."
        : "Every rival kingdom was wiped off the map.";
    const subtitle =
      winnerName === null
        ? `${reason} No kingdom survived.`
        : won
          ? `${reason} You finished first on Glory.`
          : `${reason} ${winnerName} finished first on Glory.`;

    header.append(el("p", "endscreen__subtitle", subtitle));
    return header;
  }

  private buildDismiss(): HTMLElement {
    const button = el("button", "endscreen__dismiss");
    button.type = "button";
    button.textContent = "Dismiss";
    // Dismiss rather than close: the session is over either way, and a player
    // who wants to look at the final map should be able to.
    button.addEventListener("click", () => this.hide());
    return button;
  }
}

/**
 * The highest-scoring kingdom still standing.
 *
 * Mirrors the server's `determineWinner` rather than taking `standings[0]`: an
 * eliminated kingdom keeps whatever it banked when it lost its last hex, and
 * "you won after being wiped off the map" is not a result anyone accepts. This
 * is presentation only — the server already decided the session.
 */
function findWinner(
  standings: readonly GloryScore[],
  byId: ReadonlyMap<KingdomId, KingdomState>,
): KingdomId | null {
  for (const score of standings) {
    if (byId.get(score.kingdomId)?.eliminated === true) continue;
    return score.kingdomId;
  }
  return null;
}

function buildTable(
  standings: readonly GloryScore[],
  byId: ReadonlyMap<KingdomId, KingdomState>,
  ownKingdomId: KingdomId,
): HTMLElement {
  const table = el("table", "standings");

  const head = el("thead");
  const headRow = el("tr");
  for (const [label, className] of [
    ["#", "standings__rank"],
    ["Kingdom", "standings__name"],
    ["Territory", "standings__num"],
    ["Buildings", "standings__num"],
    ["Banked", "standings__num"],
    ["Trade", "standings__num"],
    ["Glory", "standings__num standings__total"],
  ] as const) {
    const cell = el("th", className, label);
    cell.scope = "col";
    headRow.append(cell);
  }
  head.append(headRow);

  const body = el("tbody");
  standings.forEach((score, index) => {
    const kingdom = byId.get(score.kingdomId);
    const isOwn = score.kingdomId === ownKingdomId;

    const row = el("tr", "standings__row");
    row.classList.toggle("is-you", isOwn);
    row.classList.toggle("is-eliminated", kingdom?.eliminated === true);

    row.append(el("td", "standings__rank", `${index + 1}`));

    const nameCell = el("td", "standings__name");
    const swatch = el("span", "standings__swatch");
    swatch.style.background = formatColor(kingdom?.color ?? 0x999999);
    nameCell.append(swatch, el("span", "standings__label", kingdom?.name ?? score.kingdomId));
    if (isOwn) nameCell.append(el("span", "standings__tag", "you"));
    else if (kingdom?.isBot === true) nameCell.append(el("span", "standings__tag", "ai"));
    if (kingdom?.eliminated === true) {
      nameCell.append(el("span", "standings__tag is-bad", "eliminated"));
    }
    row.append(nameCell);

    row.append(
      el("td", "standings__num", formatAmount(score.territory)),
      el("td", "standings__num", formatAmount(score.buildings)),
      el("td", "standings__num", formatAmount(score.banked)),
      el("td", "standings__num", formatAmount(score.tradeVolume)),
      el("td", "standings__num standings__total", formatAmount(score.total)),
    );

    body.append(row);
  });

  table.append(head, body);
  return table;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
